import { useState, useCallback, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import WelcomePage from "@/components/pages/WelcomePage";
import SetScenario from "@/components/pages/SetScenario";
import MarketEvent1 from "@/components/pages/MarketEvent1";
import MarketEvent2 from "@/components/pages/MarketEvent2";
import MarketEvent3 from "@/components/pages/MarketEvent3";
import SideBySide from "@/components/pages/SideBySide";
import TheExhale from "@/components/pages/TheExhale";
import SignUpPage from "@/components/pages/SignUpPage";
import PrivacyPage from "@/components/pages/PrivacyPage";
import TermsPage from "@/components/pages/TermsPage";
import DoNotSellPage from "@/components/pages/DoNotSellPage";
import CookiePage from "@/components/pages/CookiePage";
import AdminLogin from "@/components/admin/AdminLogin";
import AdminDashboard from "@/components/admin/AdminDashboard";
import Companies from "@/pages/Companies";
import UseCase from "@/pages/UseCase";
import ReferenceTerms from "@/pages/Terms";
import ReferenceLayout from "@/components/ReferenceLayout";
import CookieBanner from "@/components/CookieBanner";
import PageTransition from "@/components/PageTransition";
import { AudioProvider, useAudio } from "@/components/AudioContext";
import { MusicControl } from "@/components/AudioControls";
import CotDashboard from "@/components/CotDashboard";
import IndexPage from "@/pages/IndexPage";
import TreasuryCurvePage from "@/pages/TreasuryCurvePage";
import GuidePage from "@/pages/GuidePage";
import CotProPage from "@/pages/CotProPage";
import LoginPage from "@/pages/LoginPage";
import SubscribePage from "@/pages/SubscribePage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import SetPasswordPage from "@/pages/SetPasswordPage";
import ApiDocsPage from "@/pages/ApiDocsPage";
import { AuthProvider, useAuth } from "@/components/AuthContext";
import { API_BASE } from "@/apiBase";

const BACKEND_URL = API_BASE;

function trackEvent(event) {
  const sessionId = sessionStorage.getItem("pord_session_id") || (() => {
    const id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("pord_session_id", id);
    return id;
  })();
  fetch(`${BACKEND_URL}/api/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event, session_id: sessionId }),
  }).catch(() => {});
}

/* Protected route — requires login + active subscription */
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight: "100vh", background: "var(--pord-bg, #1a1e14)" }} />;
  if (!user) return <Navigate to="/" replace />;
  const sub = user.subscription;
  if (!sub?.active) return <Navigate to="/subscribe" replace />;
  return children;
}

/* Auth-only route — requires login but not necessarily active subscription */
function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight: "100vh", background: "var(--pord-bg, #1a1e14)" }} />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

/* Redirect logged-in users away from login page to dashboard */
function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  if (loading) return <div style={{ minHeight: "100vh", background: "var(--pord-bg, #1a1e14)" }} />;
  if (user && redirect) return <Navigate to={redirect} replace />;
  if (user) return <Navigate to="/positioning" replace />;
  return children;
}

function Experience() {
  const savedStage = sessionStorage.getItem("pord_stage");
  const savedData = sessionStorage.getItem("pord_session");
  const initialStage = savedStage ? parseInt(savedStage, 10) : 0;
  const initialData = savedData ? JSON.parse(savedData) : { threshold: null, e1: null, e2: null, e3: null, feeling: null };

  const [stage, setStage] = useState(initialStage);
  const [transitioning, setTransitioning] = useState(false);
  const [sessionData, setSessionData] = useState(initialData);
  const [exhaleReached, setExhaleReached] = useState(initialStage > 5);
  const [signupCompleted, setSignupCompleted] = useState(false);
  const { stopStageAudio } = useAudio();

  useEffect(() => {
    sessionStorage.setItem("pord_stage", String(stage));
    sessionStorage.setItem("pord_session", JSON.stringify(sessionData));
  }, [stage, sessionData]);

  useEffect(() => {
    if (!sessionStorage.getItem("pord_visit_tracked")) {
      trackEvent("visit");
      sessionStorage.setItem("pord_visit_tracked", "true");
    }
  }, []);

  useEffect(() => {
    if (stage === 7 && !sessionStorage.getItem("pord_reached_signup_tracked")) {
      trackEvent("reached_signup");
      sessionStorage.setItem("pord_reached_signup_tracked", "true");
    }
  }, [stage]);

  const updateSession = useCallback((key, value) => {
    setSessionData(prev => ({ ...prev, [key]: value }));
  }, []);

  const goToStage = useCallback((nextStage) => {
    setTransitioning(true);
    setTimeout(() => {
      setStage(nextStage);
      setTransitioning(false);
      window.scrollTo({ top: 0, behavior: "instant" });
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "instant" }));
    }, 350);
  }, []);

  const goNext = useCallback(() => {
    stopStageAudio();
    if (stage === 5) setExhaleReached(true);
    goToStage(stage + 1);
  }, [stage, goToStage, stopStageAudio]);

  const goBack = useCallback(() => {
    stopStageAudio();
    goToStage(stage - 1);
  }, [stage, goToStage, stopStageAudio]);

  const pages = [
    <WelcomePage key="welcome" onNext={goNext} stageIndex={0} />,
    <SetScenario key="scenario" sessionData={sessionData} updateSession={updateSession} onNext={goNext} onBack={goBack} stageIndex={1} />,
    <MarketEvent1 key="event1" sessionData={sessionData} updateSession={updateSession} onNext={goNext} onBack={goBack} stageIndex={2} />,
    <MarketEvent2 key="event2" sessionData={sessionData} updateSession={updateSession} onNext={goNext} onBack={goBack} stageIndex={3} />,
    <MarketEvent3 key="event3" sessionData={sessionData} updateSession={updateSession} onNext={goNext} onBack={goBack} stageIndex={4} />,
    <SideBySide key="sidebyside" sessionData={sessionData} updateSession={updateSession} onNext={goNext} onBack={goBack} stageIndex={5} />,
    <TheExhale key="exhale" sessionData={sessionData} onNext={goNext} stageIndex={6} />,
    <SignUpPage key="signup" sessionData={sessionData} backendUrl={BACKEND_URL} stageIndex={7} onBack={goBack} completed={signupCompleted} setCompleted={setSignupCompleted} />,
  ];

  return (
    <div className="page-wrapper">
      <PageTransition isTransitioning={transitioning}>
        {pages[stage]}
      </PageTransition>
      <CookieBanner />
    </div>
  );
}

function App() {
  return (
    <AudioProvider>
      <AuthProvider>
        <BrowserRouter>
          <MusicControl />
          <Routes>
            {/* Root shows login page */}
            <Route path="/" element={<GuestRoute><LoginPage /></GuestRoute>} />
            <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
            <Route path="/preview" element={<Experience />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/do-not-sell" element={<DoNotSellPage />} />
            <Route path="/cookie" element={<CookiePage />} />
            <Route path="/index" element={<IndexPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/guide" element={<GuidePage />} />

            {/* Public pricing / payment pages */}
            <Route path="/subscribe" element={<SubscribePage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />

            {/* Protected — requires sign in */}
            <Route path="/positioning" element={<ProtectedRoute><CotProPage /></ProtectedRoute>} />
            <Route path="/reference" element={<ProtectedRoute><ReferenceLayout><Companies /></ReferenceLayout></ProtectedRoute>} />
            <Route path="/reference/use-case" element={<ProtectedRoute><ReferenceLayout><UseCase /></ReferenceLayout></ProtectedRoute>} />
            <Route path="/reference/terms" element={<ProtectedRoute><ReferenceLayout><ReferenceTerms /></ReferenceLayout></ProtectedRoute>} />
            <Route path="/cot" element={<ProtectedRoute><ReferenceLayout><CotDashboard /></ReferenceLayout></ProtectedRoute>} />
            <Route path="/treasury" element={<ProtectedRoute><ReferenceLayout><TreasuryCurvePage /></ReferenceLayout></ProtectedRoute>} />

            {/* Admin */}
            <Route path="/admin" element={<AdminLogin backendUrl={BACKEND_URL} />} />
            <Route path="/admin/dashboard" element={<AdminDashboard backendUrl={BACKEND_URL} />} />

            {/* Developer Docs — requires login */}
            <Route path="/developer" element={<AuthRoute><ApiDocsPage /></AuthRoute>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </AudioProvider>
  );
}

export default App;
